/**
 * The credential the two BROKER-facing surfaces use, and the freshness rule behind it.
 *
 * Its own module for a testing reason that is really a CI reason. Exercising this through the
 * `services/auth` barrel means loading `@aws-sdk/client-cognito-identity-provider` into an
 * apps/console spec, and that destabilises the vitest worker on CI — exit 1 mid-suite, no failing
 * test named, output truncated (see `apps/console/CLAUDE.md`; bisected once already across five
 * PRs). The logic worth testing here is pure decision-making, so it takes its two effects as
 * parameters and the spec needs no SDK, no `localStorage` and no clock.
 */

/**
 * Refresh a little BEFORE expiry, not at it.
 *
 * A token that passes the check and then expires during the request is the same 401 this exists to
 * prevent, and a helmet video stream is a long-lived request.
 */
export const ACCESS_TOKEN_SKEW_SECONDS = 60

/**
 * `exp` out of a JWT, or null when the token is not one we can read.
 *
 * Unreadable is reported as null and treated by the caller as "refresh it": a token we cannot parse
 * is one we cannot vouch for, and the cost of an unnecessary refresh is one round trip while the
 * cost of sending a dead credential is a failed emergency call.
 */
export function accessTokenExpiry(token: string): number | null {
  try {
    const payload = token.split('.')[1]

    if (!payload) return null

    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '='))
    const exp = (JSON.parse(json) as { exp?: unknown }).exp

    return typeof exp === 'number' ? exp : null
  } catch {
    return null
  }
}

/**
 * When the next proactive refresh should run, derived from the token the app is ACTUALLY sending.
 *
 * `useAuth` used to re-arm on a flat `setInterval(45 minutes)`, a number chosen for Cognito, whose
 * access token lives an hour and whose refresh token lives thirty days. On-prem the provider is
 * Keycloak, and the realm this ships with answers `accessTokenLifespan = 300` with
 * `ssoSessionIdleTimeout = 1800` — so the keep-alive fired a quarter of an hour AFTER the SSO
 * session had already idled out, and could never once succeed. The only thing holding an on-prem
 * session open was the user clicking (each `client.ts` 401-refresh-retry resets Keycloak's idle
 * timer); leave a tab for thirty minutes and both credentials were dead:
 *
 *   keycloak  REFRESH_TOKEN_ERROR  error="invalid_token" reason="Token is not active"
 *   backend   401 ERR_JWT_EXPIRED  "exp" claim timestamp check failed
 *
 * Measured on the gisify.app install 2026-10-16. Reading `exp` makes the cadence the provider's
 * business rather than a constant that happens to suit one of them — about four minutes on
 * Keycloak, about fifty-nine on Cognito — and keeps the idle timer alive for an untouched tab.
 *
 * An unreadable token yields the fallback rather than null: the same reasoning as
 * `accessTokenExpiry` above — a token we cannot vouch for is one worth refreshing, and the cost is
 * one round trip.
 */
export const TOKEN_REFRESH_FALLBACK_MS = 4 * 60 * 1000

/**
 * The floor. `exp` already in the past yields a negative delay, and `setTimeout` treats that as
 * "run now" — which, on a provider that is refusing the refresh, is a tight loop against the token
 * endpoint. Also covers the clock-skew case where a token arrives already half-spent.
 */
export const MIN_TOKEN_REFRESH_DELAY_MS = 5_000

/** Back-off after a refresh that failed but left the session recoverable (offline, 5xx, throttle). */
export const TOKEN_REFRESH_RETRY_MS = 30 * 1000

/** Milliseconds until the token in hand should be refreshed. See {@link TOKEN_REFRESH_FALLBACK_MS}. */
export function refreshDelayMs(token: string | null, nowMs: number): number {
  const exp = token ? accessTokenExpiry(token) : null

  if (exp === null) return TOKEN_REFRESH_FALLBACK_MS

  return Math.max(MIN_TOKEN_REFRESH_DELAY_MS, (exp - ACCESS_TOKEN_SKEW_SECONDS) * 1000 - nowMs)
}

export interface ValidAccessTokenDeps {
  /** Read the stored access token. */
  read: () => string | null
  /** Mint a new one. Must PERSIST it where `read` will find it; its return value is ignored. */
  refresh: () => Promise<unknown>
  /** Injectable clock, so the skew boundary is testable without waiting an hour. */
  now?: () => number
}

/** In-flight refresh, so N simultaneous callers cause ONE network round trip. */
let inFlight: Promise<unknown> | null = null

/** Test seam: the single-flight latch is module state and would otherwise leak between cases. */
export function __resetValidAccessTokenForTests(): void {
  inFlight = null
}

/**
 * An access token that is actually still valid, refreshing it first if it is not.
 *
 * A plain `localStorage` read has no notion of expiry, which is correct for callers that only ask
 * whether a session exists. It is NOT correct for the surfaces that authenticate directly against
 * the LIVE BROKER — the helmet video stream and the SOS voice call — because those bypass
 * `services/api/client.ts` and inherit none of its 401-refresh-retry. A Cognito access token lives
 * one hour; the console does not, so any tab open longer than that sent a dead credential and the
 * broker answered `401 token expired`.
 *
 * That failure hides in the one place an operator would look. The websocket authenticates ONCE at
 * connect and stays up, so the roster keeps showing the helmet "online" while the camera pane is
 * black and — the part that matters — Accept on an SOS alarm fails signalling. Observed against a
 * live helmet on 2026-09-07.
 *
 * Single-flighted: a fleet dashboard mounts several broker-authenticated panes at once, and each
 * racing its own refresh is both wasteful and a good way to have Cognito throttle the one
 * credential the call needs.
 */
export async function resolveValidAccessToken(deps: ValidAccessTokenDeps): Promise<string | null> {
  const token = deps.read()
  const nowSeconds = (deps.now ? deps.now() : Date.now()) / 1000

  if (token) {
    const exp = accessTokenExpiry(token)

    if (exp !== null && exp - ACCESS_TOKEN_SKEW_SECONDS > nowSeconds) return token
  }

  try {
    if (!inFlight) {
      inFlight = deps.refresh().finally(() => {
        inFlight = null
      })
    }

    await inFlight
  } catch {
    // Refreshing must never be WORSE than not refreshing. The real `refreshToken()` answers null
    // rather than throwing, but it is reached through the provider `pick()`, which throws outright
    // when the auth config has not loaded — so this had a way to fail that a plain token read never
    // had, and it would have failed the SOS CALL rather than the token read.
  }

  // The stale token as a last resort, NOT null. A credential the server can refuse is strictly more
  // useful than none: it produces `401 token expired`, which the console reports and the operator
  // can act on, where sending nothing produces a call that fails for no stated reason.
  return deps.read() ?? token
}
